/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";

import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { fireDB } from "../../../Backend/Firebase/FirebaseConfigData";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Loader2, BookOpen, Trash2, Edit3, PlusCircle } from "lucide-react";

export default function UpdateLibraryPage() {
  const { librarySlug } = useParams();
  const navigate = useNavigate();

  const [libraryData, setLibraryData] = useState<any>(null);
  const [books, setBooks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const toSlug = (str: string) =>
    str
      .toLowerCase()
      .trim()
      .replace(/ /g, "-")
      .replace(/_/g, "-")
      .replace(/[^\w-]+/g, "");

  // FETCH LIBRARY + BOOKS
  const fetchLibrary = async () => {
    if (!librarySlug) return;
    setLoading(true);

    try {
      const libRef = doc(fireDB, "NewBlogs", librarySlug);
      const libSnap = await getDoc(libRef);

      if (libSnap.exists()) {
        setLibraryData({ ...libSnap.data(), slug: libSnap.id });
      }

      const booksSnap = await getDocs(collection(fireDB, "NewBlogs", librarySlug, "books"));
      setBooks(
        booksSnap.docs.map((d) => ({
          id: d.id,
          title: d.data().name || d.data().title || "Untitled", // Use name/title
        }))
      );
    } catch (err) {
      toast.error("Error fetching library");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLibrary();
  }, [librarySlug]);

  // SAVE / RENAME + SEO SAVE
  const saveLibrary = async () => {
    const oldSlug = librarySlug!;
    const newSlug = toSlug(libraryData.slug);

    try {
      // IF slug unchanged → save fields
      if (oldSlug === newSlug) {
        await setDoc(
          doc(fireDB, "NewBlogs", oldSlug),
          {
            title: libraryData.title,
            slug: newSlug,

            // SEO
            metaTitle: libraryData.metaTitle,
            metaDescription: libraryData.metaDescription,
            metaKeywords: libraryData.metaKeywords,
            canonicalUrl: `https://forumde.in/${newSlug}`,
          },
          { merge: true }
        );

        toast.success("Updated Successfully!");
        navigate(`/update/${newSlug}`);
        return;
      }

      // RENAME DOCUMENT
      const oldRef = doc(fireDB, "NewBlogs", oldSlug);
      const oldData = await getDoc(oldRef);

      if (!oldData.exists()) {
        toast.error("Library not found");
        return;
      }

      const newRef = doc(fireDB, "NewBlogs", newSlug);

      await setDoc(newRef, {
        ...oldData.data(),
        title: libraryData.title,
        slug: newSlug,

        // SEO
        metaTitle: libraryData.metaTitle,
        metaDescription: libraryData.metaDescription,
        metaKeywords: libraryData.metaKeywords,
        canonicalUrl: `https://forumde.in/${newSlug}`,
      });

      // COPY BOOKS + CHAPTERS
      const booksSnap = await getDocs(collection(fireDB, "NewBlogs", oldSlug, "books"));
      for (const book of booksSnap.docs) {
        await setDoc(doc(fireDB, "NewBlogs", newSlug, "books", book.id), book.data());

        const chaptersSnap = await getDocs(
          collection(fireDB, "NewBlogs", oldSlug, "books", book.id, "chapters")
        );

        for (const chapter of chaptersSnap.docs) {
          await setDoc(
            doc(fireDB, "NewBlogs", newSlug, "books", book.id, "chapters", chapter.id),
            chapter.data()
          );
        }
      }

      // DELETE OLD DOC
      await deleteDoc(oldRef);

      toast.success("Library renamed successfully!");
      navigate(`/update/${newSlug}`);
    } catch (err) {
      toast.error("Error updating library");
      console.error(err);
    }
  };

  // DELETE BOOK
  const deleteBook = async (bookId: string) => {
    if (!window.confirm("Delete book?")) return;
    await deleteDoc(doc(fireDB, "NewBlogs", librarySlug!, "books", bookId));
    fetchLibrary();
    toast.success("Book deleted");
  };

  if (loading)
    return (
      <div className="flex justify-center pt-20">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );

  if (!libraryData)
    return <p className="p-6 text-lg text-red-600">❌ Library Not Found</p>;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-10 animate-fadeIn">
      <ToastContainer position="top-center" />

      {/* HEADER + SEO */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-8 rounded-2xl text-white shadow-xl">
        <h1 className="text-4xl font-bold mb-6">Update Library</h1>

        <input
          value={libraryData.title}
          onChange={(e) =>
            setLibraryData({ ...libraryData, title: e.target.value, slug: toSlug(e.target.value) })
          }
          className="w-full p-4 rounded-xl mb-4 text-black font-semibold"
          placeholder="Library Title"
        />

        {/* SEO FIELDS */}
        <div className="mt-6 bg-white/20 p-5 rounded-xl space-y-4">
          <input
            value={libraryData.metaTitle || ""}
            onChange={(e) => setLibraryData({ ...libraryData, metaTitle: e.target.value })}
            className="w-full p-3 rounded-xl text-black font-semibold"
            placeholder="Meta Title"
          />

          <textarea
            value={libraryData.metaDescription || ""}
            onChange={(e) =>
              setLibraryData({ ...libraryData, metaDescription: e.target.value })
            }
            className="w-full p-3 rounded-xl text-black font-semibold"
            rows={3}
            placeholder="Meta Description"
          />

          <input
            value={libraryData.metaKeywords || ""}
            onChange={(e) =>
              setLibraryData({ ...libraryData, metaKeywords: e.target.value.split(",") })
            }
            className="w-full p-3 rounded-xl text-black font-semibold"
            placeholder="Keywords (comma separated)"
          />
        </div>

        <button
          onClick={saveLibrary}
          className="bg-white text-blue-700 px-8 py-3 rounded-xl text-lg font-bold hover:bg-gray-100 transition mt-6"
        >
          💾 Save Changes
        </button>
      </div>

      {/* BOOK LIST */}
      <div className="bg-white p-8 shadow-xl rounded-2xl">
        <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <BookOpen /> Books
        </h2>

        {books.map((book) => (
          <div
            key={book.id}
            className="p-5 border rounded-xl flex justify-between items-center mb-4 hover:shadow-lg"
          >
            <h3 className="text-xl font-bold">{book.title}</h3>

            <div className="flex gap-4">
              <button
                onClick={() => navigate(`/updatebook/${librarySlug}/${book.id}`)}
                className="bg-blue-600 px-5 py-2 rounded-xl text-white flex items-center gap-2"
              >
                <Edit3 size={18} /> Update
              </button>

              <button
                onClick={() => deleteBook(book.id)}
                className="bg-red-600 px-5 py-2 rounded-xl text-white flex items-center gap-2"
              >
                <Trash2 size={18} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ADD BOOK */}
      <div className="text-center">
        <button
          onClick={() => navigate(`/createLibrary/${librarySlug}/create-book`)}
          className="bg-green-600 hover:bg-green-700 text-white px-10 py-4 rounded-xl text-lg shadow-xl flex items-center gap-2 mx-auto"
        >
          <PlusCircle size={22} /> Add New Book
        </button>
      </div>
    </div>
  );
}
